import { DateInput, Field, MoneyInput, Select, TextInput } from '../components/index.js';
import { AccountPicker } from '../components/AccountPicker.js';
import { fromApi, isErr, money, useFields, useSave } from '../lib/form.js';
import { t } from '../lib/i18n.js';
import { master, submit, today } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function TransferSheet() {
  const m = master.value;
  const all = [...m.accounts.values()].filter((a) => a.active);
  const cash = all.filter((a) => a.kind === 'cash');
  const banks = all.filter((a) => a.kind !== 'cash');
  const [f, set] = useFields({
    type: 'deposit',
    from: banks[0]?.id ?? '',
    to: banks[0]?.id ?? '',
    cash: cash[0]?.id ?? '',
    amount: '',
    date: today.value,
    note: '',
  });
  const [error, busy, save] = useSave();
  const needsFrom = f.type !== 'deposit';
  const needsTo = f.type !== 'withdraw';

  const onSave = () =>
    save(async () => {
      const amount = money(f.amount, 'amount', { min: 1 });
      if (isErr(amount)) return amount;
      const fromAccountId = f.type === 'deposit' ? f.cash : f.from;
      const toAccountId = f.type === 'withdraw' ? f.cash : f.to;
      if (!fromAccountId || !toAccountId) return { message: t('transfer_addFirst') };
      const r = await submit('transfer', {
        date: f.date,
        fromAccountId,
        toAccountId,
        amount,
        note: f.note,
      });
      if (!r.ok) return fromApi(r.error);
      return t('transfer_saved');
    });

  return (
    <Sheet title={t('sheet_bankMovement')} footer={<SaveButton busy={busy} onClick={onSave} />}>
      <Field label={t('transfer_type')}>
        <Select value={f.type} onChange={(v) => set('type', v)}>
          <option value="deposit">{t('transfer_deposit')}</option>
          <option value="withdraw">{t('transfer_withdraw')}</option>
          <option value="transfer">{t('transfer_transfer')}</option>
        </Select>
      </Field>
      {cash.length > 1 && (
        <Field label={t('transfer_cashAccount')}>
          <AccountPicker
            value={f.cash}
            onChange={(v) => set('cash', v)}
            accounts={cash}
            testId="account-cash"
          />
        </Field>
      )}
      <div class="grid-3">
        {needsFrom && (
          <Field label={t('transfer_fromAccount')}>
            <AccountPicker
              value={f.from}
              onChange={(v) => set('from', v)}
              accounts={banks}
              invalid={error?.field === 'fromAccountId'}
              testId="account-from"
            />
          </Field>
        )}
        {needsTo && (
          <Field label={t('transfer_toAccount')}>
            <AccountPicker
              value={f.to}
              onChange={(v) => set('to', v)}
              accounts={banks}
              invalid={error?.field === 'toAccountId'}
              testId="account-to"
            />
          </Field>
        )}
        <Field label={t('amountTaka')}>
          <MoneyInput
            value={f.amount}
            onInput={(v) => set('amount', v)}
            invalid={error?.field === 'amount'}
            autoFocus
          />
        </Field>
      </div>
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('transfer_noteRef')}>
          <TextInput value={f.note} onInput={(v) => set('note', v)} placeholder={t('optional')} />
        </Field>
      </div>
      {banks.length === 0 && <div class="help">{t('transfer_noBank')}</div>}
      <FormErrorLine error={error} />
    </Sheet>
  );
}
